<script lang="ts" setup>
    import type { LeaderboardEntry, LeaderboardResponse } from '~/types/scoring/Leaderboard';

    const props = defineProps<{
        highscores: LeaderboardResponse | null;
    }>();

    const { t } = useI18n();

    const showAll = ref(false);
    const MAX_VISIBLE_TOP = 3;

    type DisplayedRow =
        | {
              kind: 'entry';
              game: LeaderboardEntry;
              rank: number;
          }
        | {
              kind: 'ellipsis';
              id: string;
              isToggle?: boolean;
          };

    const isGlobal = computed(() => props.highscores?.scope === 'global');

    const computedDate = (timestamp: Date | null | undefined) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString();
    };

    const computedTime = (timestamp: Date | null | undefined) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const gameColumns = computed(() => {
        if (!props.highscores?.topScores?.length) {
            return [];
        }

        const ids = new Set<string>();

        for (const entry of props.highscores.topScores) {
            for (const gameScore of entry.gameScores ?? []) {
                ids.add(gameScore.gameId);
            }
        }

        return Array.from(ids);
    });

    const getGameScore = (entry: LeaderboardEntry, gameId: string) => {
        return entry.gameScores?.find((item) => item.gameId === gameId)?.score ?? 0;
    };

    const displayedRows = computed<DisplayedRow[]>(() => {
        const entries = props.highscores?.topScores || [];

        if (entries.length === 0) {
            return [];
        }

        const hasMoreThanTop = entries.length > MAX_VISIBLE_TOP;

        if (showAll.value) {
            const rows: DisplayedRow[] = entries.map((game, index) => ({
                kind: 'entry',
                game,
                rank: index + 1
            }));

            if (hasMoreThanTop) {
                rows.push({ kind: 'ellipsis', id: 'ellipsis-collapse', isToggle: true });
            }

            return rows;
        }

        if (!hasMoreThanTop) {
            return entries.map((game, index) => ({
                kind: 'entry',
                game,
                rank: index + 1
            }));
        }

        const indicesToShow = new Set<number>();
        for (let i = 0; i < Math.min(MAX_VISIBLE_TOP, entries.length); i++) {
            indicesToShow.add(i);
        }

        const currentIndex = entries.findIndex((score) => score.isCurrentUser);
        if (currentIndex !== -1) {
            for (let offset = -1; offset <= 1; offset++) {
                const idx = currentIndex + offset;
                if (idx >= 0 && idx < entries.length) {
                    indicesToShow.add(idx);
                }
            }
        }

        const sortedIndices = Array.from(indicesToShow).sort((a, b) => a - b);

        const rows: DisplayedRow[] = [];
        let lastIndex = -1;

        sortedIndices.forEach((index) => {
            if (lastIndex !== -1 && index - lastIndex > 1) {
                rows.push({ kind: 'ellipsis', id: `ellipsis-${lastIndex}-${index}` });
            }

            if (!entries[index]) return;

            rows.push({
                kind: 'entry',
                game: entries[index],
                rank: index + 1
            });

            lastIndex = index;
        });

        if (lastIndex < entries.length - 1) {
            rows.push({ kind: 'ellipsis', id: `ellipsis-end-${lastIndex}`, isToggle: true });
        }

        return rows;
    });

    const toggleLabel = computed(() => (showAll.value ? t('ranking.actions.collapse') : t('ranking.actions.expand')));

    const rowKey = (row: DisplayedRow) => (row.kind === 'entry' ? `entry-${row.rank}` : row.id);

    const colSpan = computed(() => {
        return isGlobal.value ? 3 + gameColumns.value.length : 5;
    });
</script>

<template>
    <div class="ranking-table">
        <div class="ranking-table__surface">
            <table
                v-if="displayedRows.length"
                class="table"
            >
                <thead>
                    <tr v-if="!isGlobal">
                        <th>{{ t('ranking.table.rank') }}</th>
                        <th>{{ t('ranking.table.nickname') }}</th>
                        <th>{{ t('ranking.table.score') }}</th>
                        <th>{{ t('ranking.table.date') }}</th>
                        <th>{{ t('ranking.table.time') }}</th>
                    </tr>
                    <tr v-else>
                        <th>{{ t('ranking.table.rank') }}</th>
                        <th>{{ t('ranking.table.nickname') }}</th>
                        <th
                            v-for="gameId in gameColumns"
                            :key="gameId"
                        >
                            {{ gameId }}
                        </th>
                        <th>{{ t('ranking.table.score') }}</th>
                    </tr>
                </thead>

                <tbody>
                    <template
                        v-for="row in displayedRows"
                        :key="rowKey(row)"
                    >
                        <tr
                            v-if="row.kind === 'entry' && !isGlobal"
                            :class="{ 'current-user': row.game.isCurrentUser }"
                        >
                            <td>{{ row.rank }}</td>
                            <td>
                                {{ row.game.nickname || t('ranking.anonymous') }}
                                {{ row.game.isCurrentUser ? ` ${t('ranking.salutation')}` : '' }}
                            </td>
                            <td>{{ row.game.score }}</td>
                            <td>{{ computedDate(row.game.timestamp) }}</td>
                            <td>{{ computedTime(row.game.timestamp) }}</td>
                        </tr>

                        <tr
                            v-else-if="row.kind === 'entry' && isGlobal"
                            :class="{ 'current-user': row.game.isCurrentUser }"
                        >
                            <td>{{ row.rank }}</td>
                            <td>
                                {{ row.game.nickname || t('ranking.anonymous') }}
                                {{ row.game.isCurrentUser ? ` ${t('ranking.salutation')}` : '' }}
                            </td>
                            <td
                                v-for="gameId in gameColumns"
                                :key="gameId"
                            >
                                {{ getGameScore(row.game, gameId) }}
                            </td>
                            <td>{{ row.game.score }}</td>
                        </tr>

                        <tr
                            v-else
                            class="ellipsis-row"
                        >
                            <td :colspan="colSpan">
                                <PrimaryButton
                                    v-if="row.isToggle"
                                    size="small"
                                    class="table__toggle-inline"
                                    :retain-size="true"
                                    :button-text="toggleLabel"
                                    @click="showAll = !showAll"
                                />
                                <span v-else>...</span>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/memory-game/ranking-table.scss';
</style>
